import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'
import type { MeetingViewModel } from './use-meeting-view-model'

/**
 * Identity for a meeting's notepad, longest-lived id first. The calendar event
 * outlives the recording, so a scheduled meeting keeps one key while live, once
 * saved, and when reopened from History.
 *
 * The live session ranks above the note id on purpose: a Custom Meeting has no
 * event and gains its note id only at save time, and re-keying mid-meeting
 * would orphan notes the user is looking at. `meeting-resolution` files the
 * session under the note the moment it saves, so the key holds through the
 * save and through a later reopen that knows only the note.
 *
 * The gap left is a Custom Meeting reopened after a restart: the index is
 * renderer-local, so the session is gone and the key falls to the note, leaving
 * those notes parked under the session — locally. The HIG-5815 server record
 * bridges it: the saved note carries the final notepad document, and an empty
 * local notepad seeds from it (see `use-meeting-notepad`).
 *
 * A meeting with no identity at all keys on the view id, which is shared across
 * meetings and outlives none of them. That only happens before identity
 * resolves, which is well under a keystroke, so it's left alone rather than
 * given a second storage path.
 *
 * Its own module, and a type-only import of the model, so this stays testable:
 * `use-meeting-notepad` reaches the panel atoms and the model reaches the chat
 * API, neither of which loads outside a renderer.
 */
export function meetingNotepadKey(model: MeetingViewModel): string {
  return model.calendarEvent?.id ?? model.audioSessionId ?? model.audioNoteId ?? MEETING_VIEW_ID
}
